"use client";

import { TEditorType, useCustomEditor } from "@/providers/custom-editor-provider";
import { EditorContent, JSONContent, useEditor } from "@tiptap/react";
import { CommonEditorProps } from "./base-editor.types";
import EditorBubbleMenu from "./components/editor-bubble-menu";
import EditorOptions from "./components/editor-options";

// Default Extension Imports
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Highlight from "@tiptap/extension-highlight";
import LinkExtension from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
import StarterKit from "@tiptap/starter-kit";

// Custom Extension Imports
import { cn, iterateOverNodeFromJsonContent } from "@/lib/utils";
import { all, createLowlight } from "lowlight";
import { useEffect } from "react";
import { MentionProp } from "@/types/post";
import CustomImage from "../extensions/custom-image";
import EmojiExtension from "../extensions/emoji";
import ProfileMentionExtension from "../extensions/profile-mention";
import { SlashCommand } from "../extensions/slash-command/slash-command";
import { SmartLink } from "../extensions/smart-link/smart-link";
import { Vimeo } from "../extensions/vimeo/vimeo";

interface BaseEditorProps extends CommonEditorProps {
	content?: string | JSONContent;
	placeholder?: string;
	classNames?: {
		editor?: string;
	};
	handleOnUpdate: ({
		html,
		json,
		mentions,
	}: {
		html: string;
		json: JSONContent;
		mentions?: MentionProp[];
	}) => void;
}

const lowlight = createLowlight(all);

export const getEditorConfig = ({
	type,
	placeholder,
}: {
	type: TEditorType;
	placeholder?: string;
}) => {
	const baseExtensions = [
		StarterKit.configure({
			heading: {
				HTMLAttributes: {
					class: "content-heading",
				},
				levels: [1, 2, 3],
			},
			bulletList: {
				keepMarks: true,
				keepAttributes: false,
			},
			orderedList: {
				keepMarks: true,
				keepAttributes: false,
			},
		}),
		Underline,
		Placeholder.configure({
			placeholder: placeholder || "Write something",
		}),
		// NOTE: Table extension is not working as expected when editor is initialized after switching editor type, so it's added to default extensions
		Table.configure({
			resizable: true,

			HTMLAttributes: {
				class: "content-table",
			},
		}),
		TableRow,
		TableHeader,
		TableCell,
	];

	switch (type) {
		case "discussion":
		case "basic":
			return {
				extensions: [
					...baseExtensions,
					LinkExtension.configure({
						HTMLAttributes: {
							class: "content-link",
						},
						openOnClick: false,
						autolink: true,
					}),
					EmojiExtension,
					ProfileMentionExtension,
					TextAlign.configure({
						types: ["heading", "paragraph"],
					}),
				],
				allowedFormats: [
					"bold",
					"italic",
					"link",
					"underline",
					"strike",
					"bulletList",
					"orderedList",
					"blockquote",
					"codeBlock",
					"undo",
					"redo",
					"clearContent",
				],
				maxLength: 5000,
				editorProps: {},
			};

		case "article":
		case "advanced":
			return {
				extensions: [
					...baseExtensions,
					LinkExtension.configure({
						HTMLAttributes: {
							class: "content-link",
						},
						openOnClick: false,
						autolink: true,
					}),
					Youtube.configure({
						HTMLAttributes: {
							class: "w-full aspect-video content-youtube",
						},
						inline: false,
						origin: "www.youtube.com",
					}),
					Placeholder.configure({
						placeholder: placeholder || "Write something",
					}),
					TextAlign.configure({
						types: ["heading", "paragraph"],
					}),
					CodeBlockLowlight.configure({
						lowlight,
					}),
					Superscript,
					Subscript,
					Highlight,
					EmojiExtension,
					ProfileMentionExtension,
					Vimeo,
					SlashCommand,
					SmartLink.configure({
						displayAs: "card",
					}),
					CustomImage,
				],
				allowedFormats: [
					"undo",
					"redo",
					"bold",
					"italic",
					"link",
					"color",
					"table",
					"bulletList",
					"orderedList",
					"blockquote",
					"codeBlock",
					"background",
					"horizontalRule",
					"clearContent",
					"image",
					"youtube",
				],
				maxLength: 30000,
			};

		case "comment":
			return {
				extensions: [
					...baseExtensions,
					LinkExtension.configure({
						HTMLAttributes: {
							class: "content-link",
						},
						openOnClick: false,
						autolink: true,
					}),
					ProfileMentionExtension,
					EmojiExtension,
				],
				allowedFormats: ["bold", "italic", "link", "underline", "image"],
				maxLength: 1000,
			};

		default:
			return {
				extensions: baseExtensions,
				allowedFormats: [],
				maxLength: 0,
			};
	}
};

const extractMentions = (json: JSONContent) => {
	const mentions: MentionProp[] = [];

	iterateOverNodeFromJsonContent({
		jsonContent: json,
		func: (node) => {
			if (node.type === ProfileMentionExtension.name) {
				const mention = {
					username: node.attrs?.id,
					name: node.attrs?.label,
				};
				mentions.push(mention);
			}
		},
	});

	return mentions;
};

const BaseEditor = ({ content, placeholder, classNames, handleOnUpdate }: BaseEditorProps) => {
	const { editorType, setEditor } = useCustomEditor();

	const config = getEditorConfig({ type: editorType, placeholder });

	const editor = useEditor(
		{
			content,
			extensions: config.extensions,
			onUpdate: ({ editor }) => {
				handleOnUpdate({
					html: editor.getHTML(),
					json: editor.getJSON(),
					mentions: extractMentions(editor.getJSON()),
				});
			},
			editorProps: {
				attributes: {
					class: cn("tiptap min-h-[240px] overflow-y-auto", classNames?.editor),
				},
				...config.editorProps,
			},
			immediatelyRender: false,
		},
		[editorType, placeholder]
	);

	useEffect(() => {
		setEditor(editor);

		// Clean up function
		return () => {
			if (editor) {
				setEditor(null);
			}
		};
	}, [editor, setEditor]);

	return (
		<div>
			<EditorOptions allowedFormats={config.allowedFormats} />

			<div className="mt-2" />

			<EditorBubbleMenu />

			<EditorContent editor={editor} />
		</div>
	);
};

export default BaseEditor;
